import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { FF_CLIENT_OLD_VERSION_RELOAD } from '@/consts/featureFlags';
import { posthog } from '@/providers/posthog';
import { CREATED_BY } from '@/state/nodes/nodes.state';
import type { ComponentProps, ReactNode } from 'react';
import type { NodeId } from 'web';
import type { ShortcutKey } from '@/consts/shortcuts';
import type { ButtonContained } from '@/UI/Buttons/AppButton';
import type { CreatedByFilter } from '@/hooks/useSavedSectionCreatedByFilter';

// The saved-section "created by" filter is a per-user preference (one key, not per workspace),
// shared by the left panel and the add-node menu through this store and persisted in localStorage.
const SAVED_SECTION_FILTER_KEY = 'savedSectionCreatedByFilter';
// Exported for tests. The one-time reset for users who had the old per-workspace
// value lives here: the old key stored a JSON-quoted `"me"`, which this reader
// does not match, so those users fall back to "all" once.
export const readSavedSectionCreatedByFilter = (): CreatedByFilter => {
  try {
    return localStorage.getItem(SAVED_SECTION_FILTER_KEY) === CREATED_BY.ME ? CREATED_BY.ME : CREATED_BY.ALL;
  } catch {
    return CREATED_BY.ALL;
  }
};

// Like Figma's shortcuts panel, used shortcuts are remembered per browser rather than per account.
const USED_SHORTCUTS_KEY = 'usedKeyboardShortcuts';

export type UsedShortcuts = Partial<Record<ShortcutKey, true>>;

// Exported for tests.
export const readUsedShortcuts = (): UsedShortcuts => {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(USED_SHORTCUTS_KEY) ?? '{}');
    return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
  } catch {
    return {};
  }
};

type ButtonContainedMode = ComponentProps<typeof ButtonContained>['mode'];

export interface AlertData {
  severity?: 'default' | 'success' | 'error' | 'info';
  text: ReactNode;
  icon: ReactNode;
  showCloseButton?: boolean;
  action?: {
    text: string;
    onClick: () => void | Promise<void>;
    mode?: ButtonContainedMode;
  } | null;
}

export interface SnackbarData extends AlertData {
  isOpen: boolean;
  duration?: number | null; // null = never auto-hide
  key?: string;
  minWidth?: number | string; // optional min width (e.g., 300 or '300px')
}

export interface DeleteGroupDialogData {
  type: 'deleteGroup';
  allNodeIdsToDelete: NodeId[];
  /** When true, confirm runs delete-with-bridge (Shift+Delete) instead of normal delete. */
  preserveBridgingConnections?: boolean;
}

export type DialogData = DeleteGroupDialogData;

/** What opened or closed the shortcuts panel. Opens send it with their analytics event. */
export type ShortcutsPanelToggleSource =
  'keyboard' | 'file_menu' | 'figma_desktop_menu' | 'figma_help_widget' | 'close_button';

interface GlobalState {
  snackbarData: SnackbarData;
  updateSnackbarData: (snackbarData: Partial<SnackbarData>) => void;
  updateSnackbarText: (text: ReactNode) => void;
  dialogData?: DialogData;
  openDialog: (dialog: DialogData) => void;
  closeDialog: () => void;
  isShortcutsPanelOpen: boolean;
  setIsShortcutsPanelOpen: (isOpen: boolean, source: ShortcutsPanelToggleSource) => void;
  /** Shortcuts used at least once in this browser, highlighted in the shortcuts panel. */
  usedShortcuts: UsedShortcuts;
  /** The latest shortcut use. `id` changes on every use, so the open panel can animate repeat presses too. */
  lastShortcutUse?: { key: ShortcutKey; id: number };
  markShortcutUsed: (key: ShortcutKey) => void;
  isShowRefreshAlert: boolean;
  setIsShowRefreshAlert: (isShow: boolean) => void;
  savedSectionCreatedByFilter: CreatedByFilter;
  setSavedSectionCreatedByFilter: (value: CreatedByFilter) => void;
  reset: () => void;
}

export const useGlobalStore = create<GlobalState>()(
  devtools(
    (set, get) => ({
      // Initial state
      snackbarData: { text: '', isOpen: false, icon: null, showCloseButton: false, action: null },
      dialogData: undefined,
      // Actions to update the state
      updateSnackbarData: (snackbarData: Partial<SnackbarData>) =>
        set(() => ({
          snackbarData: {
            text: '',
            isOpen: false,
            icon: null,
            action: null,
            showCloseButton: false,
            ...snackbarData,
            key: snackbarData.key ?? `snackbar-${Date.now()}`,
          },
        })),
      updateSnackbarText: (text: ReactNode) => set((state) => ({ snackbarData: { ...state.snackbarData, text } })),
      openDialog: (dialog: DialogData) => set(() => ({ dialogData: dialog })),
      closeDialog: () => set(() => ({ dialogData: undefined })),
      isShortcutsPanelOpen: false,
      setIsShortcutsPanelOpen: (isOpen: boolean, source: ShortcutsPanelToggleSource) => {
        if (isOpen === get().isShortcutsPanelOpen) return;
        if (isOpen) posthog.capture('shortcuts_panel_opened', { source });
        set(() => ({ isShortcutsPanelOpen: isOpen }));
      },
      usedShortcuts: readUsedShortcuts(),
      markShortcutUsed: (key: ShortcutKey) => {
        const lastShortcutUse = { key, id: (get().lastShortcutUse?.id ?? 0) + 1 };
        // Called on every key repeat while nudging, so localStorage is only written on a shortcut's first use.
        if (get().usedShortcuts[key]) {
          set(() => ({ lastShortcutUse }));
          return;
        }
        const usedShortcuts: UsedShortcuts = { ...get().usedShortcuts, [key]: true };
        try {
          localStorage.setItem(USED_SHORTCUTS_KEY, JSON.stringify(usedShortcuts));
        } catch {
          // localStorage unavailable — keep it in memory for this session
        }
        set(() => ({ usedShortcuts, lastShortcutUse }));
      },
      isShowRefreshAlert: false,
      setIsShowRefreshAlert: (isShow: boolean) => {
        if (posthog.isFeatureEnabled(FF_CLIENT_OLD_VERSION_RELOAD)) {
          set(() => ({ isShowRefreshAlert: isShow }));
        }
      },
      savedSectionCreatedByFilter: readSavedSectionCreatedByFilter(),
      setSavedSectionCreatedByFilter: (value: CreatedByFilter) =>
        set(() => {
          try {
            localStorage.setItem(SAVED_SECTION_FILTER_KEY, value);
          } catch {
            // localStorage unavailable — keep the choice in memory for this session
          }
          return { savedSectionCreatedByFilter: value };
        }),
      reset: () =>
        set(() => ({
          snackbarData: { text: '', isOpen: false, icon: null, showCloseButton: false, action: null },
          dialogData: undefined,
          isShortcutsPanelOpen: false,
          isShowRefreshAlert: false,
        })),
    }),
    {
      name: 'Global Store',
      enabled: import.meta.env.DEV,
    },
  ),
);

/** Records that a shortcut was used, for event handlers outside React. */
export const markShortcutUsed = (key: ShortcutKey) => useGlobalStore.getState().markShortcutUsed(key);
